import { ReportOptions } from '@shared/types/report';

export type Notes =
    | ['staff_note'?, 'owner_note'?, 'special_note'?]
    | 'staff_note'
    | 'owner_note'
    | 'special_note'
    | 'All';

export interface ReportOptionsPlus extends ReportOptions {
    Notes?: Notes;
    Question?: 'show' | 'none';
    Allergens?: 'show' | 'none';
}

export interface DetailParam {
    company: string;
    order_id?: string[];
    date_from: string;
    date_to: string;
    event_id: string | string[];
}

export enum DetailTypeEnum {
    suiteInfo = 'suiteInfo',
    ado = 'ado',
    edo = 'edo',
    parStock = 'parStock',
    aggregate = 'aggregate',
    paymentInfo = 'paymentInfo',
    paymentInfoTotal = 'paymentInfoTotal',
    orderInfo = 'orderInfo',
    refund = 'refund',
    notesAndQuestion = 'notesAndQuestion',
}

export enum PdfTypeEnum {
    table = 'table',
    list = 'list',
    orderInfoList = 'orderInfoList',
    columns = 'columns',
    notes = 'notes',
    normal = 'normal',
}

export enum PaymentCode {
    CREDIT = 'CREDIT',
    CASH = 'CASH',
    // invoice
    HOUSEA = 'HOUSEA',
}
export enum PaymentStatus {
    VOIDED = 'VOIDED',
    PAID = 'PAID',
    AWAITING_TIP = 'AWAITING_TIP',
}

export interface PaymentsInfo {
    payments_code: PaymentCode;
    pay_type_name: string;
    pay_type_amount: number;
    pay_order_tips: number;
    pay_invoice_tips: number;
    pay_transaction_type: string;
    pay_time: string;
    pay_auth: string;
    pay_notes: string;
    pay_type_total: number;
    pay_type_change: number;
    pay_status: PaymentStatus;
}

export interface DetailTableHeader {
    headerName: string;
    field: string;
    cellClass?: string[];
    width?: number;
    hide?: boolean;
    hidePdf?: boolean;
}

export type SuiteOrderSummaryData = {
    [K in keyof typeof DetailTypeEnum]: SuiteOrderSummaryOptions;
};

export interface SuiteOrderSummaryOptions {
    list: Array<SuiteInfoType> | Array<QuestionType> | Array<AdoItems> | Array<SuitePaymentItem> | Array<PaymentsInfo>;
    title?: string;
    header?: DetailTableHeader[];
    total?: object;
    show: boolean;
    pdfType: PdfTypeEnum;
    hasToolTip?: boolean;
    showToggle?: boolean;
    PdfTitleSuffix?: string;
    showAllPaymentsStatus?: boolean;
    orderNumber?: string;
    columnAverage?: boolean;
    order_state?: string;
}

export interface SuiteInfoType {
    name: string;
    value: string | QuestionType[];
    id?: string;
    /** Whether the amount type is negative, default false */
    isNegative?: boolean;
    class?: string;
}

export interface QuestionType {
    question: string;
    answer: string;
    type: string;
    child_questions?: SecondaryQuestion[];
    questionIndexDes?: string;
}

export interface SecondaryQuestion extends QuestionType {
    related_type: string;
}

export interface AdoItems {
    item_name: string;
    quantity: string;
    price: string;
    unit_price: string;
    discount: string;
    total: string;
    isPackageItem?: boolean;
    items?: AdoItems[];
    level?: number;
    item_type?: string;
    modifier_name?: string;
    comment?: string;
}

export interface SuitePaymentItem {
    sub_total: string;
    tax: string;
    total: string;
    item_name?: string;
}
export type SuitePaymentKeys = 'items' | 'admin_fee' | 'gratuity' | 'discount' | 'order';

export type SuitePaymentInfo = {
    [K in SuitePaymentKeys]: SuitePaymentItem;
};

export interface DetailNote {
    staff_note: string;
    owner_note: string;
    special_note: string;
}

export interface RefundOrder {
    order_id: string;
    order_number: string;
    state: string;
    total: number;
    payment_tips: number;
    reason_text?: string;
    time__created_utc?: string;
}

export interface SuiteOrderSummaryDetail {
    order_entity_id: string;
    order_date: string;
    order_number: string;
    order_state: string;
    suite_name: string;
    account_name: string;
    poc_name: string;
    event_name: string;
    event_start_date_time: string;
    suite_representative: string;
    suite_attendant: string;
    payment_total: string;
    order_total: string;
    balance_due: string;
    payments: string;
    invoice_balance_due: string;
    ado_total: string;
    edo_total: string;
    par_stock_total: string;
    ado_items: AdoItems[];
    edo_items: AdoItems[];
    par_stock_items: AdoItems[];
    items_info: SuitePaymentInfo;
    notes: DetailNote;
    questions: QuestionType[];
    allergens?: {
        name: string;
        entity_id: string;
    }[];
    tip: string;
    showAllPaymentsStatus?: boolean;
    refund_orders: RefundOrder[];
}

export interface SuiteOrderSummaryDetailResp {
    build: string;
    data: SuiteOrderSummaryDetail[];
}
